import { AddCircleOutlineOutlined, SearchOutlined } from "@mui/icons-material";
import { Box, Collapse, Grid, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import AccessRestrictedComponent from "../../common/AccessRestrictedComponent";
import Button from "../../common/Button";
import Card from "../../common/Card";
import CustomIconButton from "../../common/CustomIconButton";
import CustomMultipleDropDown from "../../common/CustomMultipleDropDown";
import {
  AGREEMENT_REQUEST_STATUS,
  CAN_ACCESS_APPLICATION,
  USER_ROLE_ENTITY_NAMES,
} from "../../../constants/applicationConstants";
import {
  getExploreMarketProducts,
  resetSearchAgreementRequestList,
  searchAgreementRequests,
  useExploreMarketSlice,
} from "../../../redux/slice/exploreMarket/exploreMarketSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import {
  fetchPracticeLookupByUserRole,
  setSingleAlertObj,
  SingleAlertInfo,
} from "../../../redux/slice/commonSlice";
import { useAuthSlice } from "../../../redux/slice/authSlice";
import { SearchAgreementRequestsRequest } from "../../../service/ExploreMarketService";
import { createListSort } from "../../../utils/listSort";
import { formatDate } from "../../../utils/Utility";
import Table, { Row } from "../../common/CustomTable";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import { getSearchCriteriaFromSessionStorage, setSearchCriteriaToSessionStorage } from "../../../utils/StorageUtils";
import { useModalStyles } from "../../common/useModalStyles";

const agreementRequestListSort = createListSort(
  ["practiceName", "userName", "userEmail", "manufacturerName", "productName", "hcpcsCode", "status", "requestedDate"] as const,
  {
    practiceName: "practiceName",
    userName: "userName",
    userEmail: "userEmail",
    manufacturerName: "manufacturerName",
    productName: "productName",
    hcpcsCode: "hcpcsCode",
    status: "status",
    requestedDate: "requestedDate",
  }
);

const defaultSearchCriteria: SearchAgreementRequestsRequest = {
  currentPage: 0,
  itemsPerPage: 10,
  practiceIds: [],
  productIds: [],
  manufacturerIds: [],
  statuses: [],
  downloadState: false,
};

type RequestAgreementFilterForm = {
  practiceIds: number[];
  productIds: number[];
  manufacturerIds: number[];
  status: string[];
};

type AgreementRequestRow = {
  id: number | string;
  agreementRequestId: number | string;
  practiceId: number;
  productId: number;
  userName: string;
  practiceName: string;
  productName: string;
  manufacturerName: string;
  hcpcsCode: string;
  userEmail: string;
  status: string;
  rawStatus: string;
  requestedDate: string;
};

// CS-1747: Typed route state for navigation from AgreementRequestAccess email-link flow.
type RequestAgreementListRouteState = {
  agreementRequestListSearchCriteria?: Partial<SearchAgreementRequestsRequest>;
};

const RequestAgreementList = () => {
  const dispatch = useAppDispatch();
  const { navigate } = useNavigation();
  const location = useLocation();
  const { userInfo } = useAuthSlice();
  const { searchAgreementRequestList, marketData } = useExploreMarketSlice();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const [showCard, setShowCard] = useState(true);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [practiceOptions, setPracticeOptions] = useState<{ value: number; label: string }[]>([]);
  const [searchCriteria, setSearchCriteria] = useState<SearchAgreementRequestsRequest>(defaultSearchCriteria);

  const { control, handleSubmit, setValue } = useForm<RequestAgreementFilterForm>({
    defaultValues: {
      practiceIds: [],
      productIds: [],
      manufacturerIds: [],
      status: [],
    },
  });

  const routeState = (location.state as RequestAgreementListRouteState) || {};
  const prefilledCriteriaFromState = routeState.agreementRequestListSearchCriteria;

  const isAdminUser = userInfo?.entityName === USER_ROLE_ENTITY_NAMES.ADMIN;
  // CS-1747: Keep Create Agreement action visibility aligned with Agreement List UI token/permission logic.
  const canCreateAgreementAction = userInfo?.entityName === USER_ROLE_ENTITY_NAMES.ADMIN;

  const runSearch = useCallback(async (criteria: SearchAgreementRequestsRequest) => {
    const normalizedCriteria = agreementRequestListSort.normalize(criteria);
    setSearchCriteria(normalizedCriteria);
    setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.AGREEMENT_REQUEST_MANAGEMENT + "SearchCriteria" + userInfo?.userId, normalizedCriteria);
    await dispatch(searchAgreementRequests(normalizedCriteria))
      .unwrap()
      .catch(() => {
        const alertObj: SingleAlertInfo = {
          message: "Failed to load agreement requests.",
          alertType: "error",
        };
        dispatch(setSingleAlertObj(alertObj));
      });
  }, [dispatch]);

  useEffect(() => {
    if (!isAdminUser) return;
    const initData = async () => {
      let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.AGREEMENT_REQUEST_MANAGEMENT + "SearchCriteria" + userInfo?.userId);
      let initialCriteria: SearchAgreementRequestsRequest;
      
      if (location.state && prefilledCriteriaFromState) {
        initialCriteria = {
          ...defaultSearchCriteria,
          ...prefilledCriteriaFromState,
          currentPage: prefilledCriteriaFromState?.currentPage || 0,
          downloadState: false,
        };
      } else if (location.state && (location.state as any).withSearchHistory && criteria) {
        initialCriteria = criteria;
      } else {
        initialCriteria = defaultSearchCriteria;
      }

      await Promise.all([
        runSearch(initialCriteria),
        dispatch(getExploreMarketProducts(false)).unwrap(),
      ]);

      setValue("practiceIds", initialCriteria.practiceIds || []);
      setValue("productIds", initialCriteria.productIds || []);
      setValue("manufacturerIds", initialCriteria.manufacturerIds || []);
      setValue("status", initialCriteria.statuses || []);

      const practices = await dispatch(
        fetchPracticeLookupByUserRole({ activePracticeOnly: false })
      ).unwrap();
      const formattedPracticeOptions = (practices || [])
        .map((practice: any) => ({
          value: Number(practice.practiceId),
          label: String(practice.practiceName || ""),
        }))
        .filter((practice: { value: number; label: string }) => Number.isFinite(practice.value));
      setPracticeOptions(formattedPracticeOptions);
    };
    initData().catch(() => {
      const alertObj: SingleAlertInfo = {
        message: "Failed to load filter options.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(alertObj));
    });
    return () => {
      dispatch(resetSearchAgreementRequestList());
    };
  }, [isAdminUser, dispatch, runSearch, prefilledCriteriaFromState, setValue]);

  const productOptions = useMemo(() => {
    return (marketData || [])
      .map((product) => ({
        value: Number(product.productId),
        label: String(product.productName || ""),
      }))
      .filter((product) => Number.isFinite(product.value));
  }, [marketData]);

  const manufacturerOptions = useMemo(() => {
    // CS-1741: Build manufacturer filter options from already-loaded product data (no extra API call).
    const uniqueManufacturers = new Map<number, string>();

    (marketData || []).forEach((product) => {
      const manufacturerId = Number(product.manufacturerId);
      const manufacturerName = String(product.manufacturerName || "").trim();

      if (Number.isFinite(manufacturerId) && manufacturerName) {
        uniqueManufacturers.set(manufacturerId, manufacturerName);
      }
    });

    return Array.from(uniqueManufacturers.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, [marketData]);

  const onSubmit = (data: RequestAgreementFilterForm) => {
    const data2: SearchAgreementRequestsRequest = {
      practiceIds: data.practiceIds || [],
      productIds: data.productIds || [],
      manufacturerIds: data.manufacturerIds || [],
      statuses: data.status || [],
      currentPage: 0,
      itemsPerPage: searchCriteria.itemsPerPage,
      downloadState: false,
      ...agreementRequestListSort.pick(searchCriteria),
    };
    runSearch(data2);
  };

  const statusOptions = useMemo(
    () =>
      Object.values(AGREEMENT_REQUEST_STATUS).map((status) => ({
        value: status,
        label: status.replace(/_/g, " "),
      })),
    []
  );

  const handlePagination = (newPage: number) => {
    const data = {
      ...searchCriteria,
      currentPage: newPage,
    };
    runSearch(data);
  };

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = agreementRequestListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    // This list is zero-based. withSort resets other lists to page 1.
    runSearch({ ...data, currentPage: 0 });
  };

  // CS-1747 START - Create Agreement flow from agreement request list
  const handleCreateAgreement = (row: AgreementRequestRow) => {
    navigate("/create_new_agreement", {
      state: {
        openFromAgreementRequestList: true,
        agreementRequestId: Number(row.agreementRequestId),
        practiceId: Number(row.practiceId),
        productId: Number(row.productId),
      },
    });
  };

  // CS-1747 END

  const actionComponent = (row: AgreementRequestRow): JSX.Element | null => {
    if (row.rawStatus !== AGREEMENT_REQUEST_STATUS.REQUESTED || !canCreateAgreementAction) {
      return null;
    }
    return (
      <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          tooltipMessage="Create Agreement"
          tooltipPlacement="top"
          onClick={() => handleCreateAgreement(row)}
        >
          <AddCircleOutlineOutlined fontSize="small" />
        </CustomIconButton>
      </>
    );
  };

  const columns = [
    { id: "practiceName", label: "Practice Name", minWidth: 150 },
    { id: "userName", label: "User Name", minWidth: 160 },
    { id: "userEmail", label: "User Email", minWidth: 180 },
    { id: "manufacturerName", label: "Manufacturer Name", minWidth: 170 },
    { id: "productName", label: "Product Name", minWidth: 150 },
    { id: "hcpcsCode", label: "HCPCS Code", minWidth: 120 },
    { id: "status", label: "Status", minWidth: 120 },
    { id: "requestedDate", label: "Requested Date", minWidth: 170 },
    { id: "action", label: "Action", minWidth: 180 },
  ];

  const tableRows = useMemo(() => {
    return (searchAgreementRequestList?.content || []).map((item, index) => ({
      id: item.agreementRequestId ?? `${item.productId ?? "row"}-${index}`,
      agreementRequestId: item.agreementRequestId ?? `${item.productId ?? "row"}-${index}`,
      practiceId: Number(item.practiceId || 0),
      productId: Number(item.productId || 0),
      userName: `${item.userFullName || ""}`.trim() || "NA",
      practiceName: item.practiceName || "NA",
      productName: item.productName || "NA",
      manufacturerName: item.manufacturerName || "NA",
      hcpcsCode: item.hcpcsCode || "NA",
      userEmail: item.userEmail || "NA",
      rawStatus: item.status || "",
      status: item.status ? item.status.replace(/_/g, " ") : "NA",
      requestedDate: item.requestedDate
        ? formatDate(item.requestedDate, "MM/DD/YYYY hh:mm a")
        : "NA",
    }));
  }, [searchAgreementRequestList]);

  // -- Design tokens --------------------------------------------------------
  const arlBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const arlPanelBg     = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const arlHoverBg     = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const arlCardSx = {
    borderRadius: "14px",
    border: `1px solid ${arlBorderColor}`,
    backgroundColor: arlPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...arlCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    "& .MuiAutocomplete-root, & .MuiFormControl-root": {
      width: "100%",
    },
    "& .MuiTextField-root": {
      height: "42px",
      minHeight: "42px !important",
    },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": {
      padding: "0 38px 0 12px !important",
    },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      paddingLeft: "12px !important",
      paddingRight: "12px !important",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50% !important",
      transform: "translateY(-50%)",
      right: "8px !important",
    },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": {
      borderColor: `${modalTokens.inputBorder} !important`,
    },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      borderColor: `${modalTokens.accent} !important`,
      borderWidth: "1px !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)",
    },
    "& .MuiInputBase-input": {
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
    },
    "& input::selection": {
      color: modalTokens.textPrimary,
      backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)",
    },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": {
      backgroundColor: "#376c9f !important",
      "& fieldset": { borderColor: "#7fb1df !important" },
    },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": {
      WebkitTextFillColor: "#ffffff",
      caretColor: "#ffffff",
      WebkitBoxShadow: "0 0 0 100px #376c9f inset",
      borderRadius: "10px",
      transition: "background-color 5000s ease-in-out 0s",
    },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": {
      borderRadius: "999px",
      backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)",
      color: modalTokens.accent,
      border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`,
      fontWeight: 600,
    },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": {
      color: "#fff",
      borderColor: "#00B8D9",
      backgroundColor: "#00B8D9",
      boxShadow: "0 6px 22px rgba(0,184,217,0.28)",
    },
  };

  const arlTableSx = {
    "& .MuiTableContainer-root": {
      borderRadius: "12px",
      border: "none",
      backgroundColor: "transparent",
    },
    "& table": {
      
      borderCollapse: "collapse",
      borderSpacing: 0,
    },
    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`,
      py: 2,
      fontSize: "0.84rem",
    },
    "& .MuiTableHead-root .MuiTableCell-root": {
      height: "42px",
    },
    "& .MuiTableHead-root .MuiTypography-root": {
      color: "#00B8D9 !important",
      fontSize: "0.78rem",
      fontWeight: 800,
      letterSpacing: "0.02em",
    },
    "& .MuiTableBody-root .MuiTableRow-root": {
      transition: "background-color 0.18s ease, box-shadow 0.18s ease",
    },
    "& .MuiTableBody-root .MuiTableRow-root:hover": {
      backgroundColor: `${arlHoverBg} !important`,
    },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": {
      color: modalTokens.textPrimary,
      fontSize: "0.84rem",
    },
    "& .MuiIconButton-root": {
      color: isDark ? "#94a3b8" : "#475569",
      borderRadius: "8px",
      "&:hover": {
        color: modalTokens.accent,
        backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
      },
    },
    "& .MuiPaginationItem-root": {
      borderColor: arlBorderColor,
      color: modalTokens.textSecondary,
      borderRadius: "8px",
      "&.Mui-selected": {
        color: "#fff",
        backgroundColor: modalTokens.accent,
        borderColor: modalTokens.accent,
      },
    },
  };
  // -- End design tokens ----------------------------------------------------

  if (!isAdminUser) {
    return <AccessRestrictedComponent />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        px: { xs: 1, md: 3 },
        py: 2,
      }}
    >
      <Box>
        <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
          <Card sx={filterPanelSx}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item md={5/2} xs={12}>
                  <CustomMultipleDropDown
                    control={control}
                    name="practiceIds"
                    label="Practice Name"
                    placeholderText="Select practice"
                    options={practiceOptions}
                    size="small"
                  />
                </Grid>
                <Grid item md={5/2} xs={12}>
                  <CustomMultipleDropDown
                    control={control}
                    name="manufacturerIds"
                    label="Manufacturer"
                    placeholderText="Select manufacturer"
                    options={manufacturerOptions}
                    size="small"
                  />
                </Grid>
                <Grid item md={5/2} xs={12}>
                  <CustomMultipleDropDown
                    control={control}
                    name="productIds"
                    label="Product Name"
                    placeholderText="Select product"
                    options={productOptions}
                    size="small"
                  />
                </Grid>
                <Grid item md={5/2} xs={12}>
                  <CustomMultipleDropDown
                    control={control}
                    name="status"
                    label="Status"
                    placeholderText="Select status"
                    options={statusOptions}
                    size="small"
                  />
                </Grid>
                <Grid item md={2} xs={12}>
                  <Button fullWidth size="large" startIcon={<SearchOutlined />} sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }} type="submit" removeUpperCase>
                    Search
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Card>
        </Collapse>
      </Box>

      <Box mt={2}>
        <Card sx={{ ...arlCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
          <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
            <Grid item xs={12} md>
              <Typography variant="h3" fontWeight={800} color="text.primary">
                Agreement Request List
              </Typography>
            </Grid>
            <Grid item xs={12} md="auto">
              <Button variant="outlined" color="info" onClick={toggleFilterPanel} startIcon={<SearchOutlined />} fullWidth sx={outlineBtnSx} removeUpperCase>
                {showCard ? "Hide Filters" : "Show Filters"}
              </Button>
            </Grid>
          </Grid>

          <Table
            columns={columns}
            rows={tableRows}
            emptyText={"No agreement request(s) found"}
            pageNumber={handlePagination}
            uniqueValue="id"
            totalPages={searchAgreementRequestList?.totalPages || 0}
            totalElementText={`Records: ${searchAgreementRequestList?.totalElements || 0}`}
            currentPageNumber={searchCriteria.currentPage}
            actionComponent={(row: AgreementRequestRow) => actionComponent(row)}
            showMenu={true}
            sortableColumns={agreementRequestListSort.sortableColumns}
            serverSideSorting
            onSortChange={handleTableSort}
            controlledSortColumn={agreementRequestListSort.controlledColumn(searchCriteria.sortBy)}
            controlledSortOrder={searchCriteria.sortOrder ?? null}
            sx={arlTableSx}
          />
        </Card>
      </Box>
    </Box>
  );
};

export default RequestAgreementList;